import { createFileRoute, useParams, Link, Outlet } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { useJob, useCancelJob, getJobTypeName, getJobTypeIcon, type JobResponse } from '../../../api/jobs'
import { useLiveSubscription, useScopeLive } from '../../../contexts/LiveEventsContext'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Separator } from '../../../components/ui/separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible'
import { LatestExecutionLogs } from '../../../components/LatestExecutionLogs'
import { AppIcon } from '../../../components/ui/icon'
import {
  mdiClockOutline,
  mdiLoading,
  mdiCheckCircle,
  mdiAlertCircle,
  mdiCancel,
  mdiAlert,
  mdiStop,
  mdiChevronDown,
  mdiChevronRight,
  mdiEye,
  mdiCalendarClock,
  mdiCounter,
  mdiPriorityHigh,
} from '@mdi/js'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'

export const Route = createFileRoute('/$org/jobs/$jobUid')({
  component: JobDetail,
})

const statusConfig = {
  pending: { color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400', icon: mdiClockOutline, animated: false },
  running: { color: 'bg-primary/10 text-primary', icon: mdiLoading, animated: true },
  success: { color: 'bg-green-500/10 text-green-600 dark:text-green-400', icon: mdiCheckCircle, animated: false },
  failed: { color: 'bg-destructive/10 text-destructive', icon: mdiAlertCircle, animated: false },
  cancelled: { color: 'bg-muted text-muted-foreground', icon: mdiCancel, animated: false },
  lost: { color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400', icon: mdiAlert, animated: false },
  manual: { color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: mdiAlert, animated: false },
}


function JobDetail() {
  const { org, jobUid } = useParams({ from: Route.fullPath })
  const [executionsExpanded, setExecutionsExpanded] = useState(false)
  const { t } = useTranslation('jobs')

  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/jobs` },
    { title: `${t('detail.title')} ${jobUid}` }
  ])

  // Live entity notifications for THIS job's detail scope — no collection
  // scope subscription needed here (the jobs LIST page owns that; see
  // routes/$org/jobs/index.tsx). job_executions status transitions dirty
  // this exact scope via migration 094_job_events_notify's parent-uid
  // resolution, so execution status changes appear without a manual reload.
  useLiveSubscription({ entity: 'jobs', uid: jobUid })
  const isJobScopeLive = useScopeLive({ entity: 'jobs', uid: jobUid })

  const { data: job, isLoading, error } = useJob(org, jobUid, isJobScopeLive)
  const cancelJobMutation = useCancelJob(org)

  const handleCancelJob = async () => {
    try {
      await cancelJobMutation.mutateAsync(jobUid)
    } catch (error) {
      console.error('Failed to cancel job:', error)
    }
  }

  const getJobStatus = (job: JobResponse) => {
    if (job.final_status) {
      return job.final_status
    }

    if (job.executions && job.executions.length > 0) {
      const latestExecution = job.executions[0]
      return latestExecution.status
    }

    return 'pending'
  }

  const canCancelJob = (job: JobResponse) => {
    return !job.final_status || (job.final_status !== 'success' && job.final_status !== 'failed' && job.final_status !== 'cancelled')
  }

  const formatDuration = (timeMs?: number) => {
    if (!timeMs) return 'N/A'

    if (timeMs < 1000) {
      return `${timeMs}ms`
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(1)}s`
    } else {
      const minutes = Math.floor(timeMs / 60000)
      const seconds = Math.floor((timeMs % 60000) / 1000)
      return `${minutes}m ${seconds}s`
    }
  }

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <h3 className="text-lg font-medium text-destructive">{t('detail.error_loading')}</h3>
          <p className="text-destructive mt-1">{error instanceof Error ? error.message : t('common:status.error')}</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div>
        <div className="flex justify-center items-center py-8">
          <AppIcon path={mdiLoading} spin size="lg" />
          <span className="ml-2">{t('detail.loading')}</span>
        </div>
      </div>
    )
  }

  if (!job) {
    return (
      <div>
        <div className="text-center py-8">
          <h3 className="text-lg font-medium text-foreground">{t('detail.not_found')}</h3>
          <p className="text-muted-foreground mt-1">{t('detail.not_found_description')}</p>
        </div>
      </div>
    )
  }

  const status = getJobStatus(job)
  const statusInfo = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending
  const latestExecution = job.executions && job.executions.length > 0 ? job.executions[0] : null

  return (
    <>
    <div className="overflow-x-hidden">
      <PageHeader
        className="mb-6"
        icon={getJobTypeIcon(job.type) || SECTION_ICONS.jobs}
        title={`${t('detail.title')}: ${t(`types.${job.type}`, { defaultValue: getJobTypeName(job.type) })}`}
        description={
          <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Badge className={statusInfo.color}>
              <AppIcon path={statusInfo.icon} size="xs" className={`mr-1 ${statusInfo.animated ? 'motion-safe:animate-spin' : ''}`} />
              {t(`list.status_${status}`, { defaultValue: status })}
            </Badge>
            <span className="text-sm text-muted-foreground break-all">UID: {job.uid}</span>
          </span>
        }
        actions={
        <>
        {canCancelJob(job) && (
          <Button
            variant="destructive"
            onClick={handleCancelJob}
            disabled={cancelJobMutation.isPending}
          >
            <AppIcon path={mdiStop} size="md" className="mr-2" />
            {cancelJobMutation.isPending ? t('detail.cancelling') : t('detail.cancel_job')}
          </Button>
        )}
        </>
        }
      />

      <div className="grid grid-cols-1 gap-6">
        {/* Job Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.information')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <AppIcon path={mdiPriorityHigh} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.priority')}</div>
                  <div className="font-medium">{job.priority}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <AppIcon path={mdiCounter} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.attempts')}</div>
                  {/* attempt_count is counted before each run; jobs settled
                      before it existed show their executions instead. */}
                  <div className="font-medium" data-testid="job-attempt-count">
                    {job.attempt_count || job.execution_count || job.executions?.length || 0}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <AppIcon path={mdiCalendarClock} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.scheduled')}</div>
                  <div className="font-medium">{new Date(job.scheduled_at).toLocaleString()}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <AppIcon path={mdiClockOutline} size="md" />
                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.created')}</div>
                  <div className="font-medium">{new Date(job.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>

            {job.properties && (
              <>
                <Separator />
                <div>
                  <div className="text-sm text-muted-foreground mb-2">{t('detail.properties')}</div>
                  <pre className="bg-muted/50 p-3 rounded text-sm overflow-x-auto text-foreground">
                    {JSON.stringify(job.properties, null, 2)}
                  </pre>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Latest Execution */}
        {latestExecution && (
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.latest_execution')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.status')}</div>
                  {(() => {
                    const latestStatusInfo = statusConfig[latestExecution.status as keyof typeof statusConfig]
                    return (
                      <Badge className={latestStatusInfo?.color || 'bg-muted text-muted-foreground'}>
                        <AppIcon
                          path={latestStatusInfo?.icon || mdiClockOutline}
                          size="xs"
                          className={`mr-1 ${latestStatusInfo?.animated ? 'motion-safe:animate-spin' : ''}`}
                        />
                        {t(`list.status_${latestExecution.status}`, { defaultValue: latestExecution.status })}
                      </Badge>
                    )
                  })()}
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.duration')}</div>
                  <div className="font-medium">{formatDuration(latestExecution.time_spent_ms)}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.started')}</div>
                  <div className="font-medium">{new Date(latestExecution.created_at).toLocaleString()}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">{t('detail.updated')}</div>
                  <div className="font-medium">{new Date(latestExecution.updated_at).toLocaleString()}</div>
                </div>
              </div>

              {latestExecution.output && (
                <>
                  <Separator />
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">{t('detail.output')}</div>
                    <pre className="bg-muted/50 p-3 rounded text-sm overflow-x-auto text-foreground">
                      {JSON.stringify(latestExecution.output, null, 2)}
                    </pre>
                  </div>
                </>
              )}

              {latestExecution.error && latestExecution.status === 'failed' && (
                <>
                  <Separator />
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">{t('detail.error')}</div>
                    <pre className="bg-destructive/10 p-3 rounded text-sm overflow-x-auto whitespace-pre-wrap break-words text-destructive border border-destructive/20">
                      {JSON.stringify(latestExecution.error, null, 2)}
                    </pre>
                  </div>
                </>
              )}

            </CardContent>
          </Card>
        )}

        {/* Latest Execution Logs */}
        {latestExecution && (
          <LatestExecutionLogs org={org} jobUid={jobUid} execution={latestExecution} />
        )}

        {/* Execution History */}
        {job.executions && job.executions.length > 1 && (
          <Card>
            <CardHeader>
              <Collapsible open={executionsExpanded} onOpenChange={setExecutionsExpanded}>
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between cursor-pointer">
                    <CardTitle>{t('detail.execution_history', { count: job.executions.length })}</CardTitle>
                    <AppIcon
                      path={executionsExpanded ? mdiChevronDown : mdiChevronRight}
                      size="md"
                    />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {job.executions.slice(1).map((execution) => {
                        const execStatus = statusConfig[execution.status as keyof typeof statusConfig] || statusConfig.pending

                        return (
                          <div key={execution.uid} className="flex items-center justify-between p-3 border rounded">
                            <div className="flex items-center gap-4">
                              <Badge className={execStatus.color}>
                                <AppIcon path={execStatus.icon} size="xs" className={`mr-1 ${execStatus.animated ? 'motion-safe:animate-spin' : ''}`} />
                                {t(`list.status_${execution.status}`, { defaultValue: execution.status })}
                              </Badge>
                              <div className="text-sm">
                                <div>{t('detail.duration')}: {formatDuration(execution.time_spent_ms)}</div>
                                <div className="text-muted-foreground">{new Date(execution.created_at).toLocaleString()}</div>
                              </div>
                            </div>
                            <Link
                              to="/$org/jobs/$jobUid/executions/$executionUid"
                              params={{ org, jobUid: job.uid, executionUid: execution.uid }}
                              search={{ logs_limit: 100 }}
                            >
                              <Button variant="outline" size="sm">
                                <AppIcon path={mdiEye} size="sm" className="mr-1" />
                                {t('detail.view')}
                              </Button>
                            </Link>
                          </div>
                        )
                      })}
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </CardHeader>
          </Card>
        )}
      </div>
    </div>

    {/* Execution detail (see "View" above) renders here via its own route —
        this parent has no shared layout chrome with it, so the child's page
        (its own header/cards, e.g. the ai_usage cost card) simply appears
        below this job's own summary rather than replacing it. Without this
        Outlet the /executions/$executionUid child route never mounts at
        all: TanStack Router only renders nested routes through an explicit
        parent Outlet. */}
    <Outlet />
    </>
  )
}